import { useEffect, useMemo, useState } from 'react';
import _, { isArray, isNaN, isNil } from 'lodash';
import { ImageContainer } from './ImageContainer.jsx';
import { scaleTemplateToFit } from '../utils/template.utils.js';
import colors from '../theme/colors.js';

const styles = {
  wrapper: {
    display: 'flex',
    position: 'relative',
    backgroundColor: '#5f6369',
    overflow: 'hidden',
    borderColor: '#b7b7b7',
  },
  placeholder: {
    display: 'flex',
    height: '100%',
    width: '100%',
  },
};

/**
 * Renders a single image/text layer within a template using ImageContainer.
 * For text-image composites (layer has both imageUrl and text), the layer
 * fills the full stage dimensions instead of using its own bounds.
 *
 * @param {import('../../types/proposal').TemplateImageLayer & { stageWidth: number, stageHeight: number }} props
 */
const TemplateLayer = (props) => {
  const isTextImage = props.imageUrl && props.text;
  return (
    <ImageContainer
      source={props.imageUrl || props.src}
      style={{
        display: 'flex',
        objectFit: 'fill',
        position: 'absolute',
        zIndex: props.zIndex,
        height: isTextImage ? props.stageHeight : props.height,
        width: isTextImage ? props.stageWidth : props.width,
        top: isTextImage ? 0 : props.yOrigin,
        left: isTextImage ? 0 : props.xOrigin,
        transform: isTextImage ? undefined : `rotate(${props.rotationAngle}deg)`,
      }}
    />
  );
};

/**
 * @typedef {Object} TemplateProps
 * @property {string} templateId - Unique template identifier
 * @property {string} [thumbnailUrl] - Pre-rendered thumbnail URL. When available, renders as a
 *   single background image instead of compositing individual layers.
 * @property {number} width - Native template width in pixels
 * @property {number} height - Native template height in pixels
 * @property {number} renderWidth - Available container width for scaling
 * @property {number} renderHeight - Available container height for scaling
 * @property {import('../../types/proposal').TemplateLayer[]} [placeholders] - Placeholder regions
 * @property {import('../../types/proposal').TemplateImageLayer[]} [images] - Image layers
 * @property {import('../../types/proposal').TemplateTextLayer[]} [texts] - Text layers
 * @property {string} [singleOverlay] - URL for a single full-size overlay image
 * @property {boolean} [transparentBackground] - Whether the template background should be transparent
 * @property {React.CSSProperties|React.CSSProperties[]} [style] - Additional wrapper styles
 * @property {boolean} [isPreview] - When true, hides the border (used in preview contexts)
 * @property {number} [forceRenderWidth] - Override scaled width (e.g. to match another overlay's size)
 * @property {number} [forceRenderHeight] - Override scaled height (e.g. to match another overlay's size)
 * @property {"contain"|"fill"} [thumbnailResizeMode] - Resize mode for the thumbnail image (default: 'contain')
 */

/**
 * Renders a design template by compositing its layers (images, texts, placeholders)
 * scaled to fit within the given render dimensions. Falls back to a thumbnail image
 * when available. Supports forced dimensions for cross-overlay alignment.
 *
 * @param {TemplateProps} props
 */
const Template = ({
  templateId,
  thumbnailUrl,
  width,
  height,
  renderWidth,
  renderHeight,
  placeholders = [],
  images = [],
  texts = [],
  singleOverlay,
  transparentBackground,
  style,
  isPreview = false,
  forceRenderWidth,
  forceRenderHeight,
  thumbnailResizeMode = 'contain',
}) => {
  const [thumbnailPath, setThumbnailPath] = useState(null);

  useEffect(() => {
    const checkThumbnail = async () => {
      if (thumbnailUrl) {
        setThumbnailPath(thumbnailUrl);
      } else {
        setThumbnailPath(null);
      }
    };

    checkThumbnail();
  }, [templateId, thumbnailUrl]);

  const scaledTemplate = useMemo(() => {
    try {
      return scaleTemplateToFit(
        {
          width,
          height,
          placeholders,
          images,
          texts,
        },
        renderWidth,
        renderHeight
      );
    } catch (error) {
      return { width, height, placeholders, images, texts };
    }
  }, [templateId, width, height, renderWidth, renderHeight, placeholders, images, texts]);

  const borderWidth = 1;

  // When forced dimensions are provided (e.g. green screen overlay forced to main overlay size),
  // use them for the wrapper size; otherwise use scaled template dimensions.
  const wrapperWidth = forceRenderWidth && forceRenderHeight ? forceRenderWidth : scaledTemplate.width;
  const wrapperHeight = forceRenderWidth && forceRenderHeight ? forceRenderHeight : scaledTemplate.height;

  const templateStyles = useMemo(
    () =>
      _.merge(
        {},
        ...[
          styles.wrapper,
          {
            width: wrapperWidth,
            height: wrapperHeight,
            // If Template is being used in a preview we want to hide the border.
            borderWidth: !isPreview && borderWidth,
          },
          transparentBackground && { backgroundColor: 'transparent' },
        ].concat(!isNil(style) ? (isArray(style) ? style : [style]) : [])
      ),
    [wrapperWidth, wrapperHeight, borderWidth, isPreview, style, transparentBackground]
  );

  const layers = useMemo(() => {
    return [...(scaledTemplate.images ?? []), ...(scaledTemplate.texts ?? [])];
  }, [scaledTemplate.images, scaledTemplate.texts]);

  if (thumbnailPath) {
    return (
      <ImageContainer
        source={thumbnailPath}
        resizeMode={thumbnailResizeMode}
        style={{
          position: 'absolute',
          borderWidth: !isPreview && 1,
          borderColor: colors.basic05,
          height: wrapperHeight,
          width: wrapperWidth,
        }}
      />
    );
  }

  return (
    <div style={{ position: 'absolute' }}>
      <div style={templateStyles}>
        {(scaledTemplate.placeholders ?? []).map((placeholder, index) => (
          <div
            key={`placeholder${index}`}
            style={{
              display: 'flex',
              position: 'absolute',
              zIndex: placeholder.zIndex,
              backgroundColor: transparentBackground ? null : '#fff',
              height: placeholder.height,
              width: placeholder.width,

              // Adjust origin by the border width,
              // as the border itself can shift items.
              top: placeholder.yOrigin - borderWidth,
              left: placeholder.xOrigin - borderWidth,
            }}
          ></div>
        ))}
        {layers.map((layer) => (
          <TemplateLayer key={layer.id} {...layer} stageWidth={width} stageHeight={height} />
        ))}
        {!!singleOverlay && (
          <ImageContainer
            source={singleOverlay}
            style={{
              display: 'flex',
              objectFit: 'fill',
              position: 'absolute',
              zIndex: 1,
              height,
              width,
            }}
          />
        )}
      </div>
    </div>
  );
};

export default Template;
